/*
 * Keeps the free-shipping strip in step with the cart.
 *
 * The strip is rendered correctly by Liquid on every page load, so this file
 * is pure enhancement: with JavaScript off, the bar is still right on arrival
 * and simply stops updating between navigations.
 *
 * Why it patches fetch and XHR
 * ----------------------------
 * The store has no cart-change event we can rely on. The theme, the cart
 * drawer and any future app all mutate the cart through Shopify's own
 * /cart/add|change|update|clear endpoints, so those requests are the one
 * signal common to every path. Both transports are wrapped because themes and
 * apps still use each. The wrappers are deliberately inert: they return the
 * original value untouched, never swallow a rejection, and only schedule a
 * refresh after the request settles.
 */
(function () {
  'use strict';

  var CART_WRITE = /\/cart\/(?:add|change|update|clear)/i;
  var bars = [];
  var pending = null;

  function urlOf(input) {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    return (input && input.url) || '';
  }

  /* Several writes often land together (quantity steppers, bulk adds). One
     refresh on the trailing edge is enough and avoids a burst of /cart.js. */
  function scheduleRefresh() {
    if (pending) clearTimeout(pending);
    pending = setTimeout(function () {
      pending = null;
      refresh();
    }, 120);
  }

  function refresh() {
    if (!bars.length) return;
    fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root + 'cart.js'
      : '/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (cart) { if (cart) bars.forEach(function (bar) { render(bar, cart); }); })
      .catch(function () { /* leave the server-rendered state in place */ });
  }

  function money(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-CA', {
        style: 'currency',
        currency: currency || 'CAD',
      }).format(cents / 100);
    } catch (_error) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  /* Templates come from the section settings via data-* so the merchant's
     wording in the theme editor survives a client-side update. */
  function fill(template, amount) {
    return String(template || '').replace('[amount]', amount);
  }

  function render(bar, cart) {
    var threshold = parseInt(bar.el.dataset.threshold, 10);
    if (!isFinite(threshold) || threshold <= 0) return;

    var total = cart.total_price;
    var remaining = threshold - total;
    var qualified = remaining <= 0;
    var empty = cart.item_count === 0;
    var progress = Math.max(0, Math.min(100, Math.round((total / threshold) * 100)));
    var currency = cart.currency || bar.el.dataset.currency;

    if (bar.message) {
      bar.message.textContent = empty
        ? fill(bar.copy.empty, money(threshold, currency))
        : qualified
          ? bar.copy.qualified
          : fill(bar.copy.progress, money(remaining, currency));
      bar.message.dataset.qualified = String(qualified && !empty);
    }

    if (bar.track) {
      bar.track.hidden = empty;
      bar.track.setAttribute('aria-valuenow', String(progress));
      if (bar.fillEl) bar.fillEl.style.width = progress + '%';
    }

    /* Pickup is the offer that fits a small basket, so it stays visible until
       free shipping is actually earned. */
    if (bar.pickup) bar.pickup.hidden = qualified && !empty;
  }

  function collect() {
    bars = Array.prototype.map.call(
      document.querySelectorAll('.pmx-shipping-bar'),
      function (el) {
        return {
          el: el,
          message: el.querySelector('[data-role="message"]'),
          track: el.querySelector('[data-role="track"]'),
          fillEl: el.querySelector('[data-role="fill"]'),
          pickup: el.querySelector('[data-role="pickup"]'),
          copy: {
            empty: el.dataset.copyEmpty,
            progress: el.dataset.copyProgress,
            qualified: el.dataset.copyQualified,
          },
        };
      }
    );
  }

  function wrapFetch() {
    if (!window.fetch || window.fetch.__pmxShippingBar) return;
    var original = window.fetch;
    var wrapped = function (input) {
      var result = original.apply(this, arguments);
      if (CART_WRITE.test(urlOf(input))) {
        result.then(scheduleRefresh, function () { /* failed write changes nothing */ });
      }
      return result;
    };
    wrapped.__pmxShippingBar = true;
    window.fetch = wrapped;
  }

  function wrapXHR() {
    var proto = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
    if (!proto || proto.open.__pmxShippingBar) return;
    var open = proto.open;
    var wrapped = function (method, url) {
      if (CART_WRITE.test(String(url || ''))) {
        this.addEventListener('load', scheduleRefresh);
      }
      return open.apply(this, arguments);
    };
    wrapped.__pmxShippingBar = true;
    proto.open = wrapped;
  }

  function start() {
    collect();
    if (!bars.length) return;
    wrapFetch();
    wrapXHR();
    /* Back/forward cache restores a stale DOM with a possibly changed cart. */
    window.addEventListener('pageshow', function (event) { if (event.persisted) refresh(); });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') refresh();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
